// src/pages/surveys/components/QuestionEditor.js
import React, { useState } from 'react';

const s = {
  labelInput: {
    width: '100%',
    border: 'none',
    borderBottom: '2px solid var(--ka-gray-200)',
    outline: 'none',
    fontFamily: 'var(--font-display)',
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--ka-navy)',
    background: 'transparent',
    paddingBottom: 10,
    marginBottom: 16,
    lineHeight: 1.4,
  },
  descInput: {
    width: '100%',
    border: 'none',
    outline: 'none',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--ka-gray-400)',
    background: 'transparent',
    marginBottom: 12,
  },
  fakeInput: {
    width: '100%',
    padding: '11px 14px',
    border: '1px solid var(--ka-gray-200)',
    borderRadius: 8,
    fontSize: 14,
    color: 'var(--ka-gray-400)',
    background: 'var(--ka-gray-50)',
    fontFamily: 'var(--font-body)',
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  optionInput: {
    flex: 1,
    border: 'none',
    borderBottom: '1px solid var(--ka-gray-200)',
    outline: 'none',
    padding: '7px 0',
    fontSize: 15,
    fontFamily: 'var(--font-body)',
    color: 'var(--ka-navy)',
    background: 'transparent',
  },
  removeOpt: {
    background: 'none',
    border: 'none',
    color: 'var(--ka-gray-400)',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: 1,
    padding: '2px 6px',
  },
  addOpt: {
    background: 'none',
    border: 'none',
    color: 'var(--ka-navy)',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    padding: '6px 0',
    marginTop: 4,
  },
  typeTag: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--ka-gold)',
    marginBottom: 12,
    background: 'rgba(200,151,58,0.1)',
    padding: '4px 10px',
    borderRadius: 4,
  },
  required: {
    display: 'inline-block',
    fontSize: 12,
    color: '#ef4444',
    marginLeft: 6,
    fontWeight: 700,
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 16,
    paddingTop: 12,
    borderTop: '1px solid var(--ka-gray-100)',
  },
  actionBtn: {
    background: 'none',
    border: '1px solid var(--ka-gray-200)',
    borderRadius: 6,
    padding: '6px 14px',
    fontSize: 13,
    cursor: 'pointer',
    color: 'var(--ka-gray-600)',
    fontFamily: 'var(--font-body)',
    transition: 'all 0.1s',
  },
  ratingRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 10,
  },
  ratingCircle: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: '2px solid var(--ka-gray-200)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--ka-gray-400)',
  },
  sectionLine: {
    borderTop: '2px solid var(--ka-gray-200)',
    margin: '4px 0 14px',
  },
};

export function QuestionPreview({ question }) {
  if (question.type === 'section') {
    return (
      <div style={{ minHeight: 60, width: '100%' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ka-gold)', marginBottom: 6 }}>
          Section Break
        </div>
        <div style={{ fontSize: 17, fontWeight: 600, color: question.label ? 'var(--ka-navy)' : 'var(--ka-gray-300)', fontFamily: 'var(--font-display)', fontStyle: question.label ? 'normal' : 'italic' }}>
          {question.label || 'Click to add section title'}
        </div>
      </div>
    );
  }

  const TYPE_LABELS_LOCAL = {
    short_text: 'Short Text', long_text: 'Paragraph', multiple_choice: 'Multiple Choice',
    checkboxes: 'Checkboxes', dropdown: 'Dropdown', rating: 'Rating Scale', date: 'Date', number: 'Number',
    hyperlink: 'Hyperlink',
  };

  const previewText = {
    short_text: 'Short answer',
    long_text: 'Paragraph answer',
    multiple_choice: question.options?.length ? question.options.slice(0, 2).join(' · ') + (question.options.length > 2 ? ' …' : '') : 'Multiple choice',
    checkboxes: question.options?.length ? question.options.slice(0, 2).join(' · ') + (question.options.length > 2 ? ' …' : '') : 'Checkboxes',
    dropdown: question.options?.length ? question.options.slice(0, 2).join(' · ') + (question.options.length > 2 ? ' …' : '') : 'Dropdown',
    rating: `Rating 1–${question.ratingMax || 5}`,
    date: 'Date',
    number: 'Number',
    hyperlink: question.linkUrl || 'No URL set',
  }[question.type] || question.type;

  return (
    <div style={{ minHeight: 60, width: '100%' }}>
      <div style={{
        display: 'inline-block',
        background: 'rgba(196,155,73,0.12)',
        color: 'var(--ka-gold)',
        border: '1px solid rgba(196,155,73,0.3)',
        borderRadius: 20,
        padding: '2px 10px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        marginBottom: 6,
      }}>
        {TYPE_LABELS_LOCAL[question.type] || question.type}
        {question.required && <span style={{ color: '#e53935', marginLeft: 3 }}>*</span>}
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, color: question.label ? 'var(--ka-navy)' : 'var(--ka-gray-300)', fontFamily: 'var(--font-display)', marginBottom: 4, fontStyle: question.label ? 'normal' : 'italic' }}>
        {question.label || 'Click to add question text'}
      </div>
      <div style={{ fontSize: 13, color: 'var(--ka-gray-400)', fontFamily: 'var(--font-body)' }}>
        {previewText}
      </div>
    </div>
  );
}

const TYPE_LABELS = {
  short_text: 'Short Text',
  long_text: 'Paragraph',
  multiple_choice: 'Multiple Choice',
  checkboxes: 'Checkboxes',
  dropdown: 'Dropdown',
  rating: 'Rating Scale',
  date: 'Date',
  number: 'Number',
  section: 'Section Break',
  hyperlink: 'Hyperlink',
};

export default function QuestionEditor({
  question, active, onChange,
  onDelete, onMoveUp, onMoveDown, onDuplicate,
  isFirst, isLast,
}) {
  const addOption = () => {
    const opts = [...(question.options || []), `Option ${(question.options?.length || 0) + 1}`];
    onChange({ options: opts });
  };

  const updateOption = (idx, val) => {
    const opts = [...question.options];
    opts[idx] = val;
    onChange({ options: opts });
  };

  const removeOption = (idx) => {
    onChange({ options: question.options.filter((_, i) => i !== idx) });
  };

  if (question.type === 'section') {
    return (
      <div>
        <div style={s.sectionLine} />
        <input
          style={{ ...s.labelInput, fontSize: 17, fontFamily: 'var(--font-display)' }}
          value={question.label}
          onChange={e => onChange({ label: e.target.value })}
          placeholder="Section Title"
        />
        <input
          style={s.descInput}
          value={question.description || ''}
          onChange={e => onChange({ description: e.target.value })}
          placeholder="Section description (optional)"
        />
        {active && (
          <div style={s.actionBar}>
            <button style={s.actionBtn} onClick={onMoveUp} disabled={isFirst}>↑</button>
            <button style={s.actionBtn} onClick={onMoveDown} disabled={isLast}>↓</button>
            <button style={{ ...s.actionBtn, color: '#ef4444' }} onClick={onDelete}>Delete</button>
          </div>
        )}
      </div>
    );
  }

  if (question.type === 'hyperlink') {
    return (
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ka-gold)', marginBottom: 10, background: 'rgba(200,151,58,0.1)', display: 'inline-block', padding: '4px 10px', borderRadius: 4 }}>
          🔗 Hyperlink
        </div>
        <input
          style={{ ...s.labelInput, fontSize: 15 }}
          value={question.label || ''}
          onChange={e => onChange({ label: e.target.value })}
          placeholder="Description (e.g. Download the form here)"
          onClick={e => e.stopPropagation()}
        />
        {/* Live preview of the link */}
        <div style={{ padding: '10px 14px', background: 'var(--ka-gray-50)', borderRadius: 8, border: '1px solid var(--ka-gray-200)' }}>
          {question.linkUrl ? (
            <a
              href={question.linkUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--ka-navy)', fontWeight: 600, fontSize: 14, textDecoration: 'underline' }}
              onClick={e => e.stopPropagation()}
            >
              {question.linkText || question.linkUrl}
            </a>
          ) : (
            <span style={{ color: 'var(--ka-gray-400)', fontSize: 13, fontStyle: 'italic' }}>
              Add a URL in the side panel →
            </span>
          )}
        </div>
        {active && (
          <div style={s.actionBar}>
            <button style={s.actionBtn} onClick={onMoveUp} disabled={isFirst}>↑</button>
            <button style={s.actionBtn} onClick={onMoveDown} disabled={isLast}>↓</button>
            <button style={{ ...s.actionBtn, color: '#ef4444' }} onClick={onDelete}>Delete</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={s.typeTag}>
        {TYPE_LABELS[question.type] || question.type}
        {question.required && <span style={s.required}>*</span>}
      </div>

      <input
        style={s.labelInput}
        value={question.label}
        onChange={e => onChange({ label: e.target.value })}
        placeholder="Question"
        onClick={e => e.stopPropagation()}
      />

      {question.description ? (
        <div style={{ fontSize: 12, color: 'var(--ka-gray-400)', marginBottom: 10 }}>
          {question.description}
        </div>
      ) : null}

      {/* Preview of answer area */}
      {question.type === 'short_text' && (
        <div style={s.fakeInput}>{question.placeholder || 'Short answer text'}</div>
      )}

      {question.type === 'long_text' && (
        <div style={{ ...s.fakeInput, minHeight: 72 }}>{question.placeholder || 'Long answer text'}</div>
      )}

      {question.type === 'number' && (
        <div style={{ ...s.fakeInput, width: 140 }}>{question.placeholder || '0'}</div>
      )}

      {question.type === 'date' && (
        <div style={{ ...s.fakeInput, width: 200 }}>📅 MM / DD / YYYY</div>
      )}

      {(question.type === 'multiple_choice' || question.type === 'checkboxes') && (
        <div>
          {(question.options || []).map((opt, idx) => (
            <div key={idx} style={s.optionRow}>
              <span style={{ fontSize: 14, color: 'var(--ka-gray-400)' }}>
                {question.type === 'multiple_choice' ? '○' : '☐'}
              </span>
              <input
                style={s.optionInput}
                value={opt}
                onChange={e => updateOption(idx, e.target.value)}
                onClick={e => e.stopPropagation()}
              />
              <button style={s.removeOpt} onClick={() => removeOption(idx)}>×</button>
            </div>
          ))}
          <button style={s.addOpt} onClick={addOption}>+ Add option</button>
        </div>
      )}

      {question.type === 'dropdown' && (
        <div>
          {(question.options || []).map((opt, idx) => (
            <div key={idx} style={s.optionRow}>
              <span style={{ fontSize: 13, color: 'var(--ka-gray-400)', minWidth: 20 }}>{idx + 1}.</span>
              <input
                style={s.optionInput}
                value={opt}
                onChange={e => updateOption(idx, e.target.value)}
                onClick={e => e.stopPropagation()}
              />
              <button style={s.removeOpt} onClick={() => removeOption(idx)}>×</button>
            </div>
          ))}
          <button style={s.addOpt} onClick={addOption}>+ Add option</button>
        </div>
      )}

      {question.type === 'rating' && (
        <div>
          <div style={s.ratingRow}>
            {Array.from({ length: question.ratingMax || 5 }, (_, i) => (
              <div key={i} style={s.ratingCircle}>{i + 1}</div>
            ))}
          </div>
          {(question.ratingLabels?.low || question.ratingLabels?.high) && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--ka-gray-400)' }}>
              <span>{question.ratingLabels.low}</span>
              <span>{question.ratingLabels.high}</span>
            </div>
          )}
        </div>
      )}

      {/* Action bar (visible when active) */}
      {active && (
        <div style={s.actionBar}>
          <button style={s.actionBtn} onClick={onDuplicate}>Duplicate</button>
          <button style={s.actionBtn} onClick={onMoveUp} disabled={isFirst}>↑</button>
          <button style={s.actionBtn} onClick={onMoveDown} disabled={isLast}>↓</button>
          <button style={{ ...s.actionBtn, color: '#ef4444', borderColor: '#fecaca' }} onClick={onDelete}>
            Delete
          </button>
        </div>
      )}
    </div>
  );
}